import { FeatureFlag } from 'types/features';

export type ReportCategory = 'overview' | 'invoicing' | 'wip' | 'employees';

export const REPORT_CATEGORIES: ReportCategory[] = ['overview', 'invoicing', 'wip', 'employees'];

export type ReportView = 'table' | 'chart';

/** A date a report's period can bound, as the report's `dateBasis` filter names it. */
export type DateBasis = 'TIMECARD_DATE' | 'INVOICE_DATE' | 'SERVICE_DATE';

/**
 * One report of the Reports module. Its page lives at `/reports/<category>/<key>`; its strings live under
 * `reporting.<key>` in the message catalogue (`title`, `description`, `about.*`).
 */
export type ReportEntry = {
    key: string;
    category: ReportCategory;
    view: ReportView;
    /**
     * The dates the report's period can bound, its default first. Two mean the report has a "Filter dates by"
     * switch; none mean it shows the current state and has no period.
     */
    dateBases: DateBasis[];
    /** A tenant's own report: hidden unless this flag is on, on top of the module's own flag. */
    featureFlag?: FeatureFlag;
    /**
     * The most rows a table report's list shows, as its backend query caps them; the totals row covers every row.
     * {@link DEFAULT_ROW_LIMIT} when unset.
     */
    rowLimit?: number;
};

export const DEFAULT_ROW_LIMIT = 1000;

export function rowLimitOf(report: ReportEntry): number {
    return report.rowLimit ?? DEFAULT_ROW_LIMIT;
}

export const REPORT_PERMISSIONS = ['READ.REPORTS.ALL'];

export const REPORTS: ReportEntry[] = [
    { key: 'billable-hours-by-year', category: 'overview', view: 'chart', dateBases: ['TIMECARD_DATE'] },
    { key: 'billable-hours-by-month', category: 'overview', view: 'chart', dateBases: ['TIMECARD_DATE'] },
    { key: 'top-cases-by-billable-hours', category: 'overview', view: 'table', dateBases: ['TIMECARD_DATE'] },
    {
        key: 'top-cases-by-invoiced-hours',
        category: 'overview',
        view: 'table',
        dateBases: ['TIMECARD_DATE', 'INVOICE_DATE'],
    },
    { key: 'top-clients-by-revenue', category: 'overview', view: 'table', dateBases: ['INVOICE_DATE', 'SERVICE_DATE'] },
    { key: 'top-payers-by-revenue', category: 'overview', view: 'table', dateBases: ['INVOICE_DATE', 'SERVICE_DATE'] },
    { key: 'revenue-by-employee', category: 'invoicing', view: 'table', dateBases: ['INVOICE_DATE'] },
    {
        key: 'invoiced-fees-by-case-and-assignment',
        category: 'invoicing',
        view: 'table',
        dateBases: ['INVOICE_DATE', 'TIMECARD_DATE'],
    },
    {
        key: 'invoiced-hourly-rates-by-employee-and-case',
        category: 'invoicing',
        view: 'table',
        dateBases: ['TIMECARD_DATE'],
    },
    {
        key: 'invoice-totals-by-accounting-type',
        category: 'invoicing',
        view: 'table',
        dateBases: ['INVOICE_DATE', 'SERVICE_DATE'],
    },
    {
        key: 'invoice-totals-by-payer-country',
        category: 'invoicing',
        view: 'table',
        dateBases: ['INVOICE_DATE', 'SERVICE_DATE'],
    },
    { key: 'draft-invoices-by-case', category: 'invoicing', view: 'table', dateBases: [] },
    { key: 'wip-by-case', category: 'wip', view: 'table', dateBases: [] },
    {
        key: 'wip-by-case-and-timecard-status',
        category: 'wip',
        view: 'table',
        dateBases: ['TIMECARD_DATE'],
        rowLimit: 10000,
    },
    { key: 'billable-hours-by-employee-and-month', category: 'employees', view: 'chart', dateBases: ['TIMECARD_DATE'] },
    { key: 'billable-hours-by-employee-and-year', category: 'employees', view: 'chart', dateBases: ['TIMECARD_DATE'] },
    { key: 'invoiced-work-by-employee-and-month', category: 'employees', view: 'chart', dateBases: ['TIMECARD_DATE'] },
    { key: 'invoiced-work-by-employee-and-year', category: 'employees', view: 'chart', dateBases: ['TIMECARD_DATE'] },
    { key: 'productivity-by-employee', category: 'employees', view: 'table', dateBases: ['TIMECARD_DATE'] },
    {
        key: 'productivity-by-employee-and-case',
        category: 'employees',
        view: 'table',
        dateBases: ['TIMECARD_DATE'],
        rowLimit: 10000,
    },
    {
        key: 'working-time-by-employee',
        category: 'employees',
        view: 'table',
        dateBases: ['TIMECARD_DATE'],
        featureFlag: FeatureFlag.REPORT_WORKING_TIME_BY_EMPLOYEE,
    },
    { key: 'hours-by-employee-and-day', category: 'employees', view: 'table', dateBases: ['TIMECARD_DATE'] },
];

export function reportPath(report: Pick<ReportEntry, 'category' | 'key'>): string {
    return `/reports/${report.category}/${report.key}`;
}

/** The all-reports page narrowed to one category. */
export function categoryPath(category: ReportCategory): string {
    return `/reports?category=${category}`;
}

export function isReportCategory(value: string | null | undefined): value is ReportCategory {
    return REPORT_CATEGORIES.includes(value as ReportCategory);
}

/**
 * The last path segment of each report's page, for the menu's Reports entry: the menu matches a route by its last
 * segment, which on a report's page is the report's key, not `reports`.
 */
export const REPORT_ACTIVE_URLS = REPORTS.map((report) => `/${report.key}`);

/** The earlier key of a renamed report to its key, so a link saved under the earlier key still opens the report. */
const EARLIER_REPORT_KEYS: Record<string, string> = {
    'performance-by-employee': 'productivity-by-employee',
    'performance-by-employee-and-case': 'productivity-by-employee-and-case',
    'revenue-by-case-and-assignment': 'invoiced-fees-by-case-and-assignment',
};

/** A report's key is unique across categories, so a link that names the report under another category still finds it. */
export function findReport(key: string | undefined): ReportEntry | undefined {
    const current = (key && EARLIER_REPORT_KEYS[key]) ?? key;
    return REPORTS.find((report) => report.key === current);
}
